import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMessage, useApiMutation, useApiQuery } from "../../api";
import type { BillingAccount, Pack, Uuid } from "../../api";
import { Badge, Button, Card, Field, useToast } from "../../components";
import { formatDateTime, formatEuros, formatTokens, isZeroAmount, parseTokenInput } from "../../format";
import { useAuthenticatedSession } from "../../session";
import { AsyncBoundary, PageHeader } from "../../shell";
import feuilles from "../feuilles/Feuilles.module.css";
import { ParametresOnglets } from "./ParametresOnglets";
import { Prelevements } from "./Prelevements";
import styles from "./Parametres.module.css";

/**
 * Le prélèvement automatique de l'entreprise, pour ses administrateurs :
 * signer un mandat SEPA (ou enregistrer une carte) chez le prestataire, régler
 * la recharge automatique, activer le pay as you go dans la limite fixée par
 * l'ESN, et suivre les prélèvements.
 */
export function PaiementPage() {
  const { company } = useAuthenticatedSession();
  if (company === null) {
    return null;
  }
  return (
    <>
      <PageHeader
        eyebrow="Paramètres"
        title="Paiement"
        description="Le moyen de paiement prélevé automatiquement, la recharge de vos tokens sous un seuil, et le pay as you go : engager des projets au-delà de vos packs, réglés en fin de mois."
      />
      <ParametresOnglets />
      <Paiement companyId={company.id} />
    </>
  );
}

function Paiement({ companyId }: { companyId: Uuid }) {
  const { notify } = useToast();
  const [params, setParams] = useSearchParams();
  const compte = useApiQuery((o) => api.getCompanyBilling(companyId, o), [companyId]);

  // Retour de la page du prestataire : on le dit une fois, puis on nettoie
  // l'URL pour qu'un rechargement ne le répète pas.
  const retour = params.get("mandat");
  useEffect(() => {
    if (retour === null) {
      return;
    }
    if (retour === "ok") {
      notify({ tone: "success", title: "Mandat enregistré", description: "Le moyen de paiement est actif dès sa confirmation par le prestataire." });
    } else {
      notify({ tone: "neutral", title: "Signature abandonnée", description: "Aucun moyen de paiement n'a été enregistré." });
    }
    setParams({}, { replace: true });
  }, [retour, notify, setParams]);

  return (
    <div className={styles.marque}>
      <AsyncBoundary state={compte} errorTitle="Paiement illisible">
        {(a) => (
          <>
            {a.suspended_at !== null && <Suspension account={a} />}
            <MoyenPaiement account={a} onReload={compte.reload} />
            <RechargeAuto account={a} onUpdated={compte.reload} />
            <PayAsYouGo account={a} onUpdated={compte.reload} />
            {/* Relue à chaque changement du compte : activer la recharge sous
                le seuil la lance aussitôt. */}
            <Prelevements key={a.updated_at} companyId={a.company_id} />
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}

function Suspension({ account }: { account: BillingAccount }) {
  return (
    <Card title="Prélèvement suspendu" subtitle={account.suspended_at !== null ? `Depuis le ${formatDateTime(account.suspended_at)}` : undefined}>
      <p className={feuilles.mention}>
        {account.suspension_reason ?? "Un prélèvement a échoué."} La recharge automatique, le pay as you go et l'achat de packs sont coupés jusqu'au règlement de la facture ; les tokens déjà acquis restent utilisables.
      </p>
    </Card>
  );
}

function MoyenPaiement({ account, onReload }: { account: BillingAccount; onReload: () => void }) {
  const { notifyError } = useToast();
  const signature = useApiMutation(api.startBillingSetup);

  useEffect(() => {
    if (signature.error !== null) {
      notifyError(signature.error, "Signature impossible");
    }
  }, [signature.error, notifyError]);

  async function signer() {
    const s = await signature.run(account.company_id);
    if (s !== null) {
      window.location.assign(s.url);
    }
  }

  const actif = account.payment_method === "active";
  return (
    <Card
      title="Moyen de paiement"
      subtitle="Un mandat de prélèvement SEPA ou une carte, enregistré chez notre prestataire de paiement. LDE ne voit jamais votre IBAN ni votre numéro de carte."
    >
      <div className={styles.formulaire}>
        <div className={styles.ligne}>
          {actif && <Badge tone="success">Actif</Badge>}
          {account.payment_method === "pending" && <Badge tone="warning">Signature en cours</Badge>}
          {account.payment_method === "none" && <Badge tone="neutral">Aucun</Badge>}
          {account.payment_method_label !== null && <span>{account.payment_method_label}</span>}
        </div>
        <div className={styles.boutons}>
          <Button variant={actif ? "secondary" : "primary"} onClick={() => void signer()} loading={signature.pending}>
            {actif ? "Changer de moyen de paiement" : "Signer un mandat SEPA ou enregistrer une carte"}
          </Button>
          {account.payment_method === "pending" && (
            <Button variant="ghost" onClick={onReload}>
              Actualiser
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function RechargeAuto({ account, onUpdated }: { account: BillingAccount; onUpdated: () => void }) {
  const { notify } = useToast();
  const catalogue = useApiQuery((o) => api.listPacks(o), []);
  const [active, setActive] = useState(account.topup_enabled);
  const [seuil, setSeuil] = useState(formatTokens(account.topup_threshold_tokens));
  const [pack, setPack] = useState(account.topup_pack_id ?? "");
  useEffect(() => {
    setActive(account.topup_enabled);
    setSeuil(formatTokens(account.topup_threshold_tokens));
    setPack(account.topup_pack_id ?? "");
  }, [account.topup_enabled, account.topup_threshold_tokens, account.topup_pack_id]);
  const reglage = useApiMutation(api.setCompanyTopUp);

  const lu = parseTokenInput(seuil);
  const sansMoyen = account.payment_method !== "active";
  const incomplet = active && (lu === null || isZeroAmount(lu) || pack === "");

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    if (lu === null || incomplet) {
      return;
    }
    const a = await reglage.run(account.company_id, { enabled: active, threshold_tokens: lu, ...(pack !== "" ? { pack_id: pack } : {}) });
    if (a === null) {
      return;
    }
    onUpdated();
    if (active && !a.topup_enabled) {
      notify({ tone: "warning", title: "Recharge refusée", description: "Le moyen de paiement a refusé la recharge, qui reste coupée. Vérifiez-le avant de la réactiver." });
      return;
    }
    notify({ tone: "success", title: a.topup_enabled ? "Recharge automatique activée" : "Recharge automatique coupée" });
  }

  const erreur =
    lu === null
      ? "Un nombre de tokens positif, deux décimales au plus."
      : active && isZeroAmount(lu)
        ? "Un seuil supérieur à zéro."
        : reglage.error !== null
          ? errorMessage(reglage.error)
          : undefined;
  const packs: Pack[] = catalogue.data?.packs ?? [];
  return (
    <Card
      title="Recharge automatique"
      subtitle="Quand votre disponible passe sous le seuil, le pack choisi est racheté et prélevé. Ses tokens sont crédités tout de suite, sans attendre l'encaissement."
    >
      <form className={styles.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
        <label className={`${feuilles.case} ${sansMoyen ? feuilles.caseGrisee : ""}`}>
          <input
            type="checkbox"
            checked={active}
            disabled={(sansMoyen && !active) || reglage.pending}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span>Activer la recharge automatique</span>
          {sansMoyen && <span className={feuilles.droit}>signez d'abord un mandat</span>}
        </label>
        <Field label="Seuil de déclenchement" hint="En tokens disponibles." error={erreur}>
          {(commande) => (
            <input {...commande} type="text" inputMode="decimal" value={seuil} onChange={(e) => setSeuil(e.target.value)} disabled={reglage.pending} />
          )}
        </Field>
        <Field label="Pack racheté" hint="Prélevé au prix du catalogue." error={active && pack === "" ? "Choisissez un pack." : undefined}>
          {(commande) => (
            <select {...commande} value={pack} onChange={(e) => setPack(e.target.value)} disabled={reglage.pending}>
              <option value="">—</option>
              {packs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} — {formatTokens(p.tokens)} tokens, {formatEuros(p.price_cents)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className={styles.boutons}>
          <Button type="submit" variant="primary" loading={reglage.pending} disabled={lu === null || incomplet}>
            Enregistrer la recharge
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PayAsYouGo({ account, onUpdated }: { account: BillingAccount; onUpdated: () => void }) {
  const { notify, notifyError } = useToast();
  const reglage = useApiMutation(api.updateCompanyBilling);

  useEffect(() => {
    if (reglage.error !== null) {
      notifyError(reglage.error, "Réglage non enregistré");
    }
  }, [reglage.error, notifyError]);

  async function basculer(on: boolean) {
    const maj = await reglage.run(account.company_id, { pay_as_you_go: on });
    if (maj === null) {
      return;
    }
    onUpdated();
    notify({ tone: "success", title: on ? "Pay as you go activé" : "Pay as you go coupé" });
  }

  const sansMoyen = account.payment_method !== "active";
  const sansLimite = isZeroAmount(account.credit_limit_tokens);
  return (
    <Card
      title="Pay as you go"
      subtitle="Quand vos packs ne suffisent plus, le manque est avancé dans la limite de crédit, au même prix qu'en pack, puis prélevé en fin de mois."
    >
      <div className={styles.formulaire}>
        <label className={`${feuilles.case} ${sansMoyen ? feuilles.caseGrisee : ""}`}>
          <input
            type="checkbox"
            checked={account.pay_as_you_go}
            disabled={(sansMoyen && !account.pay_as_you_go) || reglage.pending}
            onChange={(e) => void basculer(e.target.checked)}
          />
          <span>Activer le pay as you go</span>
          {sansMoyen && <span className={feuilles.droit}>signez d'abord un mandat</span>}
        </label>
        <p className={feuilles.mention}>
          Limite de crédit fixée par votre ESN : <strong>{formatTokens(account.credit_limit_tokens)} tokens</strong>.
          {sansLimite && " Tant qu'elle vaut zéro, le pay as you go reste inopérant ; demandez-la à votre interlocuteur."}
        </p>
      </div>
    </Card>
  );
}
